'use client'

import React, { useRef, useState } from "react";
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import { LanguageDirection, LanguageType } from "@/lib/types";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import { Check, Play, Pause } from "@phosphor-icons/react";
import Subheading from "@/components/ui/Subheading/Subheading";
import PlayerButton from "@/components/ui/PlayerButton/PlayerButton";
import MuxPlayer from "@mux/mux-player-react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { debug } from '@/lib/debug';
import { useIsLg } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger);

type TitleFont = "small" | "big";

interface LPHeroSectionProps {
  title: string
  paragraph?: string
  imageSrc: string
  imageAlt: string
  videoSrc?: string
  mediaType?: 'image' | 'video'
  buttonText?: string
  hint?: string
  language?: LanguageType
  titleFont: TitleFont
  benefits?: string[]
  testimonials?: {
    quote: string
    author: string
  }[]
  useMuxPlayer?: boolean
  muxPlaybackIdDesktop?: string
  muxPlaybackIdMobile?: string
  onOpenSlidingForm?: () => void
  id?: string
}


const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
}


const LPHeroSection: React.FC<LPHeroSectionProps> = ({
  title,
  paragraph,
  // hint,
  language = "english",
  imageSrc,
  videoSrc,
  mediaType = "image",
  buttonText,
  // itleFont,
  // aspectRatio,
  imageAlt,
  benefits,
  testimonials,
  useMuxPlayer = false,
  muxPlaybackIdDesktop,
  muxPlaybackIdMobile,
  onOpenSlidingForm,
  id,
}) => {
  const isLg = useIsLg();

  const handleSecondaryVideoToggle = () => {
    if (useMuxPlayer && secondaryMuxPlayerRef.current) {
      if (isSecondaryPlaying) {
        secondaryMuxPlayerRef.current.pause();
      } else {
        secondaryMuxPlayerRef.current.play();
      }
      setIsSecondaryPlaying(!isSecondaryPlaying);
    } else if (secondaryVideoRef.current) {
      if (isSecondaryPlaying) {
        secondaryVideoRef.current.pause();
      } else {
        secondaryVideoRef.current.play();
      }
      setIsSecondaryPlaying(!isSecondaryPlaying);
    }
    // Hide the Pause control after any play/pause toggle.
    setIsSecondaryControlsVisible(false);
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // const secondaryContentRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLImageElement>(null);
  // Add new ref for title characters
  // const titleCharsRef = useRef<HTMLSpanElement[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const secondaryVideoRef = useRef<HTMLVideoElement>(null);
  const secondaryMuxPlayerRef = useRef<any>(null);
  // const [isPlaying, setIsPlaying] = useState(true);
  // Initial state is `false` because the secondary video no longer autoplays —
  // it only starts on the first button click. Drives which icon (Play vs Pause)
  // the toggle shows: Play when !isSecondaryPlaying, Pause when isSecondaryPlaying.
  const [isSecondaryPlaying, setIsSecondaryPlaying] = useState(false);
  // Whether the Pause control is currently revealed over the playing video.
  const [isSecondaryControlsVisible, setIsSecondaryControlsVisible] = useState(false);

  // useGSAP(() => {
  //   debug.log('hero section useGSAP');
  //   const container = containerRef.current;
  //   const section = sectionRef.current;
  //   const content = contentRef.current;
  //   const titleChars = titleCharsRef.current;
  //   const secondaryContent = secondaryContentRef.current;
  //   const heroImage = heroImageRef.current;
  //   const heroVideo = videoRef.current;
  //   if (!container || !section || !content || !titleChars) return;
  //   if (mediaType === 'image' && !heroImage) return;
  //   if (mediaType === 'video' && !heroVideo) return;

  //   // Set initial states
  //   gsap.set(titleChars, {
  //     autoAlpha: 0,
  //     filter: 'blur(100px)',
  //     y: -20,
  //     force3D: true,
  //   });

  //   if (mediaType === 'image' && heroImage) {
  //     gsap.set(heroImage, {
  //       scale: isLg ? 1.1 : 1.15,
  //     });
  //   }

  //   if (mediaType === 'video' && heroVideo) {
  //     gsap.set(heroVideo, {
  //       scale: isLg ? 1.1 : 1.15,
  //     });
  //   }

  //   if (secondaryContent) {
  //     gsap.set(secondaryContent, {
  //       visibility: 'hidden'
  //     });
  //   }

  //   const startAnimations = () => {
  //     // Hero media animation
  //     if (mediaType === 'image' && heroImage) {
  //       gsap.to(heroImage, {
  //         scale: 1,
  //         duration: 3.0,
  //         ease: "power4.out",
  //       });
  //     }

  //     if (mediaType === 'video' && heroVideo) {
  //       gsap.to(heroVideo, {
  //         scale: 1,
  //         duration: 3.0,
  //         ease: "power4.out",
  //       });
  //     }

  //     // Title characters animation
  //     gsap.to(titleChars, {
  //       autoAlpha: 1,
  //       filter: 'blur(0px)',
  //       y: 0,
  //       duration: 0.8,
  //       stagger: 0.035,
  //       ease: "power2.out",
  //       force3D: true,
  //     });

  //     // Calculate total duration of title animation
  //     const titleDuration = ((titleChars.length - 1) * 0.035) + 0.8 + 0.1;

  //     // Animate down elements after title animation
  //     const mainTl = gsap.timeline();
  //     mainTl.add(() => {
  //       if (!secondaryContent) return;

  //       const downElements = secondaryContent.children;

  //       // Set initial state
  //       gsap.set(secondaryContent, {
  //         visibility: 'visible'
  //       });

  //       gsap.fromTo(downElements,
  //         {
  //           y: -50,
  //           autoAlpha: 0,
  //           force3D: true,
  //         },
  //         {
  //           y: 0,
  //           autoAlpha: 1,
  //           duration: 1,
  //           ease: "power2.out",
  //           stagger: 0.2,
  //           force3D: true,
  //         }
  //       );
  //     }, titleDuration - (0.5 + (Math.floor(titleChars.length / 10) * 0.15)));
  //   };

  //   let handleTransitionComplete: () => void;

  //   // Check if we came from a navigation transition
  //   const path = window.location.pathname;
  //   if (path === '/legal' || path === '/beauty-center') {
  //     // Start animations immediately for legal page
  //     startAnimations();
  //   } else {
  //     // For other pages, wait for navigation transition
  //     handleTransitionComplete = () => {
  //       debug.log('handling event ');
  //       startAnimations();
  //       // Remove the event listener after it's fired
  //       window.removeEventListener('navigationTransitionComplete', handleTransitionComplete);
  //     };

  //     // Check if we're loading the page directly (not from navigation)
  //     // if (document.readyState === 'complete' && !document.querySelector('.transition-overlay')) {
  //     //   debug.log('starting animations');
  //     //   startAnimations();
  //     // } else {
  //     debug.log('adding event listener');
  //     window.addEventListener('navigationTransitionComplete', handleTransitionComplete);
  //     // }
  //   }

  //   return () => {
  //     if (handleTransitionComplete) {
  //       window.removeEventListener('navigationTransitionComplete', handleTransitionComplete);
  //     }
  //   }

  // }, []);

  // Separate useGSAP for scroll animations
  // useGSAP(() => {
  //   const container = containerRef.current;
  //   const section = sectionRef.current;
  //   if (!container || !section) return;

  //   const scrollTl = gsap.timeline({
  //     scrollTrigger: {
  //       trigger: section,
  //       start: "top top",
  //       end: "+=20%",
  //       scrub: true,
  //     }
  //   });

  //   scrollTl.to(section, { padding: 0 })
  //     .to(container, { borderRadius: 0 }, "<");

  // }, { dependencies: [aspectRatio], revertOnUpdate: true });

  // Split title into words with leading spaces
  // const titleWords = title.split(' ').map((word, wordIndex) =>
  //   wordIndex === 0 ? word : ` ${word}`
  // );

  // Split each word into characters while maintaining word grouping
  // const titleCharacters = titleWords.map((word, wordIndex) => (
  //   <div
  //     key={wordIndex}
  //     style={{ display: 'inline-block', whiteSpace: 'nowrap' }}
  //   >
  //     {word.split('').map((char, charIndex) => {
  //       // Calculate absolute character index across all words
  //       const absoluteIndex = titleWords
  //         .slice(0, wordIndex)
  //         .reduce((acc, prevWord) => acc + prevWord.length, 0) + charIndex;

  //       return (
  //         <span
  //           key={`${wordIndex}-${charIndex}`}
  //           ref={el => {
  //             if (el) titleCharsRef.current[absoluteIndex] = el;
  //           }}
  //           style={{ display: 'inline-block', whiteSpace: 'pre' }}
  //         >
  //           {char}
  //         </span>
  //       );
  //     })}
  //   </div>
  // ));

  return (
    <section
      ref={sectionRef}
      id={id}
        // h-[100svh]

        // min-h-[100svh] tb:min-h-0
      className="
        hero-section
        padding-narrow-x-mobile
        padding-narrow-y-mobile
        padding-narrow-x-desktop
        padding-narrow-y-desktop
        relative
        w-full
        lg:h-[100svh]
        overflow-hidden
        z-10
    ">

      {/* Main container with background image */}
      <div
        ref={containerRef}
        className="
          relative
          w-full
          h-full
          overflow-hidden
          border-radius-main
          outline-secondary outline-stroke-soft-200
          px-[1.25rem]
          pb-[1.5rem]
          pt-[9rem]
          lg:px-[2rem]
          lg:pb-[3.5rem]
          lg:pt-[7rem]
          flex items-center justify-center
        "
      >
        {/* Background Media */}
        {mediaType === 'image' ? (
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="100vw"
            priority
            fetchPriority="high"
            ref={heroImageRef}
            className="object-cover -z-10"
            style={{ transformOrigin: 'center center' }}
          />
        ) : (
          <video
            ref={videoRef}
            src={videoSrc}
            className="absolute inset-0 w-full h-full object-cover -z-10"
            style={{ transformOrigin: 'center center' }}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
        )}

        {/* Content */}
        <div className="h-full w-full flex flex-col items-center justify-end">
          <div ref={contentRef}
          style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
            // w-full
            className="
            w-[86vw]
            vsm:w-[86vw]
            tb:w-[88vw]
            lg:w-[94vw]
            ">
            {isLg && (
              <div className="w-full h-full flex flex-row items-end justify-between gap-[1rem]">
                <div className="w-[37rem] flex-grow container-small px-[1.5rem] flex flex-col gap-[1rem]">
                  <h1 className="text-80-to-30--neue-june text-text-reversed text-balance container-small">
                    {title}
                  </h1>
                  <p className="text-20-to-14--alexandria text-text-reversed max-w-[37rem]">
                    {paragraph}
                  </p>
                  <div className="flex flex-col gap-[2rem] pt-[1rem]">
                    <div className="w-fit">
                      <PrimaryButton
                        text={buttonText ?? ''}
                        size={56}
                        type="accent"
                        onClick={() => {
                          if (onOpenSlidingForm) {
                            onOpenSlidingForm();
                          }
                        }}
                        icon={true}
                        isLPCtaButton={true}
                      />
                    </div>
                    <div className="w-full max-[49.6875rem] flex flex-row gap-[1.5rem]">
                      {(benefits || ["Personalized, non-invasive treatments", "World-exclusive beauty-tech devices ", "Hand-picked global specialists "]).map((benefit, index) => (
                        <div key={index} className="w-fit flex flex-row items-start gap-[0.5rem]">
                          <div className="p-[0.125rem]">
                            <Check className="text-graphics-reversed" size={16} />
                          </div>
                          <p className={`text-text-reversed text-14--alexandria ${language === 'arabic' ? 'text-right' : 'text-left'}`}>
                            {benefit}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="w-[17rem] max-w-[37rem] flex-grow flex flex-col gap-[1.5rem] ">
                  {(videoSrc || (useMuxPlayer && muxPlaybackIdDesktop && muxPlaybackIdMobile)) && <div className="relative w-full h-full">
                    {useMuxPlayer && muxPlaybackIdDesktop && muxPlaybackIdMobile ? (
                      <>
                        <style>{`
                          mux-player {
                            --controls: none !important;
                          }
                          mux-player::part(controls) {
                            display: none !important;
                          }
                          mux-player mux-controls {
                            display: none !important;
                          }
                          mux-player button,
                          mux-player [role="button"] {
                            display: none !important;
                          }
                        `}</style>
                        <div className="w-full h-full aspect-[16/9] border-radius-main outline-secondary outline-stroke-sub-300 overflow-hidden">
                          <MuxPlayer
                            ref={secondaryMuxPlayerRef}
                            streamType="on-demand"
                            playbackId={isLg ? muxPlaybackIdDesktop : muxPlaybackIdMobile}
                            loop
                            playsInline
                            muted
                            className="w-full h-full"
                            style={{
                              scale: 1.0,
                              "--controls": "none",
                              "--media-object-fit": "cover",
                              "--media-object-position": "center center"
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <video
                        ref={secondaryVideoRef}
                        src={videoSrc}
                        className="
                        w-full h-full object-cover
                        border-radius-main
                        border-secondary border-stroke-sub-300
                        "
                        muted
                        loop
                        playsInline
                        preload="auto"
                      />
                    )}
                    {!isSecondaryPlaying ? (
                      <div className={`absolute ${language === 'arabic' ? 'bottom-[1.5rem] left-[1.5rem]' : 'bottom-[1.5rem] right-[1.5rem]'}`}>
                        <PlayerButton
                          size={68}
                          onClick={handleSecondaryVideoToggle}
                        >
                          <Play className="text-graphics-reversed" size={24} />
                        </PlayerButton>
                      </div>
                    ) : (
                      <>
                        {/* Tap/click the playing video to reveal the Pause control */}
                        <button
                          type="button"
                          aria-label="Show controls"
                          className="absolute inset-0 cursor-pointer"
                          onClick={() => setIsSecondaryControlsVisible((v) => !v)}
                        />
                        <div
                          className={`absolute transition-opacity duration-300 ease-[ease] ${language === 'arabic' ? 'bottom-[1.5rem] left-[1.5rem]' : 'bottom-[1.5rem] right-[1.5rem]'} ${isSecondaryControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        >
                          <PlayerButton
                            size={68}
                            onClick={handleSecondaryVideoToggle}
                          >
                            <Pause className="text-graphics-reversed" size={24} />
                          </PlayerButton>
                        </div>
                      </>
                    )}
                  </div>}
                  <Swiper
                    modules={[Autoplay]}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    loop={true}
                    speed={600}
                    className="w-full h-full cursor-grab active:cursor-grabbing"
                    updateOnWindowResize={false}
                    resizeObserver={false}
                    observer={true}
                    observeParents={true}
                  >
                    {(testimonials || [{ quote: '"I never imagined beauty could feel this personal, this restorative."', author: 'Nada F.' }]).map((t, i) => (
                      <SwiperSlide key={i}>
                        <div
                         style={{
                          direction: "ltr",
                          textAlign: language === 'arabic' ? 'right' : 'left',
                          paddingInlineStart: language === 'arabic' ? '1rem' : '0',
                          paddingInlineEnd: language === 'arabic' ? '0' : '1rem',
                          }}
                         className="w-full h-full flex flex-col gap-[1.5rem]">
                          <p className={`${isLg ? 'text-20--neue-june' : 'text-14--alexandria'} text-text-reversed`}>
                            {t.quote}
                          </p>
                          <Subheading
                            size={isLg ? 20 : 16}
                            subheading={t.author}
                            alignment={language === 'arabic' ? 'right' : 'left'}
                            textColor="text-text-reversed"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            )}
            {!isLg && (
              <div className="w-full h-full flex flex-col items-end justify-between gap-[1rem]">
                <div className="w-full flex-grow container-small flex flex-col items-center gap-[1rem]">
                  <h1 className="text-80-to-30--neue-june text-text-reversed text-balance text-center">
                    {title}
                  </h1>

                  <div className="relative w-full max-w-[37rem] h-full">
                    {useMuxPlayer && muxPlaybackIdDesktop && muxPlaybackIdMobile ? (
                      <>
                        <style>{`
                          mux-player {
                            --controls: none !important;
                          }
                          mux-player::part(controls) {
                            display: none !important;
                          }
                          mux-player mux-controls {
                            display: none !important;
                          }
                          mux-player button,
                          mux-player [role="button"] {
                            display: none !important;
                          }
                        `}</style>
                        <div className="w-full h-full aspect-[16/9] border-radius-main outline-secondary outline-stroke-sub-300 overflow-hidden">
                          <MuxPlayer
                            ref={secondaryMuxPlayerRef}
                            streamType="on-demand"
                            playbackId={muxPlaybackIdDesktop}
                            loop
                            playsInline
                            muted
                            className="w-full h-full"
                            style={{
                              scale: 1.0,
                              "--controls": "none",
                              "--media-object-fit": "cover",
                              "--media-object-position": "center center"
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <video
                        ref={secondaryVideoRef}
                        src={videoSrc}
                        className="
                        w-full h-full object-cover
                        border-radius-main
                        border-secondary border-stroke-sub-300
                        "
                        muted
                        loop
                        playsInline
                        preload="auto"
                      />
                    )}
                    {!isSecondaryPlaying ? (
                      <div className={`absolute ${language === 'arabic' ? 'bottom-[1.5rem] left-[1.5rem]' : 'bottom-[1.5rem] right-[1.5rem]'}`}>
                        <PlayerButton
                          size={68}
                          onClick={handleSecondaryVideoToggle}
                        >
                          <Play className="text-graphics-reversed" size={24} />
                        </PlayerButton>
                      </div>
                    ) : (
                      <>
                        {/* Tap/click the playing video to reveal the Pause control */}
                        <button
                          type="button"
                          aria-label="Show controls"
                          className="absolute inset-0 cursor-pointer"
                          onClick={() => setIsSecondaryControlsVisible((v) => !v)}
                        />
                        <div
                          className={`absolute transition-opacity duration-300 ease-[ease] ${language === 'arabic' ? 'bottom-[1.5rem] left-[1.5rem]' : 'bottom-[1.5rem] right-[1.5rem]'} ${isSecondaryControlsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        >
                          <PlayerButton
                            size={68}
                            onClick={handleSecondaryVideoToggle}
                          >
                            <Pause className="text-graphics-reversed" size={24} />
                          </PlayerButton>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-[1rem]">
                    <div className="w-full flex flex-col items-center gap-[0.5rem]">
                      {(benefits || ["Personalized, non-invasive treatments", "World-exclusive beauty-tech devices ", "Hand-picked global specialists "]).map((benefit, index) => (
                        <div key={index} className="w-full flex flex-row items-center justify-center gap-[0.5rem]">
                          <div className="p-[0.125rem]">
                            <Check className="text-graphics-reversed" size={16} />
                          </div>
                          <p className={`text-text-reversed text-14--alexandria text-center`}>
                            {benefit}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="w-fit">
                      <PrimaryButton
                        text={buttonText ?? ''}
                        size={52}
                        type="accent"
                        onClick={() => {
                          if (onOpenSlidingForm) {
                            onOpenSlidingForm();
                          }
                        }}
                        icon={true}
                        isLPCtaButton={true}
                      />
                    </div>
                  </div>

                </div>

                <div className="w-full flex-grow flex flex-col gap-[1.5rem] ">
                  <Swiper
                    modules={[Autoplay]}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    loop={true}
                    speed={600}
                    className="w-full h-full cursor-grab active:cursor-grabbing"
                    updateOnWindowResize={false}
                    resizeObserver={false}
                    observer={true}
                    observeParents={true}
                  >
                    {(testimonials || [{ quote: '"Worth every minute on the waiting list."', author: 'SARAH K.' }]).map((t, i) => (
                      <SwiperSlide key={i}>
                        <div
                         style={{ direction: "ltr" }}
                         className="w-full h-full flex flex-col gap-[0.75rem] pt-[0.5rem] lg:pt-0 lg:pr-[1rem]">
                          <p className={`text-14--alexandria text-text-reversed text-center`}>
                            {t.quote}
                          </p>
                          <Subheading
                            size={16}
                            subheading={t.author}
                            alignment="center"
                            textColor="text-text-reversed"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            )}
          </div>


        </div>
      </div>
    </section >
  )
};

export default LPHeroSection;
